import BottomSheet from '@gorhom/bottom-sheet';
import {
  CommonActions,
  StackActions,
  useFocusEffect,
  useNavigation,
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import * as Sentry from '@sentry/react-native';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  KeyboardAvoidingView,
  TouchableOpacity,
  StyleSheet,
  ViewToken,
  BackHandler,
  Linking,
  Keyboard,
  Alert,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Avatar from '@components/Avatar/Avatar';
import ChatMessageList, {
  type ChatMessageListHandle,
} from '@components/Chat/ChatMessageList';
import { isAssignTaskTooltipActive } from '@components/Chat/JoinRequest';
import { MyChatForm } from '@components/Chat/MyChatForm';
import { TagsCollectionType } from '@components/Chat/TagsCollection';
import ChatHeader from '@components/Headers/ChatHeader';
import MemberTaskNudgeBanner from '@components/Nudge/MemberTaskNudgeBanner';
import NudgeBanner from '@components/Nudge/NudgeBanner';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import AcceptDeclineButtons from '@components/shared/Button/AcceptDeclineButtons';
import Icon from '@components/shared/Icon/Icon';
import { CreatedProjectModal } from '@components/Tutorialization/CreatedProjectModal';
import ProjectOverlay from '@components/Tutorialization/ProjectOverlay';
import TooltipPopup, {
  getTooltipAnchorSpacing,
} from '@components/Tutorialization/TooltipPopup';
import {
  Chat,
  GetMeDocument,
  Message,
  useCompleteWebTutorialFlowsMutation,
  useUpdateOnboardingStateMutation,
  useGetChatQuery,
  useSearchLazyQuery,
  WebTutorialFlow,
  UserRequestCategory,
  UserRequestable,
  useListPendingUserRequestsQuery,
} from '@graphql/generated';
import { useApolloClient } from '@hooks/useApolloClient';
import useChatAvatar from '@hooks/useChatAvatar';
import useChatInput from '@hooks/useChatInput';
import { useChatTutorial } from '@hooks/useChatTutorial';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import { useIsInvitedNewUser } from '@hooks/useInvitedUserOnboarding';
import { useKeyboardHeight } from '@hooks/useKeyboardHeight';
import { useListRecentProjectsTasksFromQuery } from '@hooks/useListProjectsTasksRecentFromQuery';
import useMe from '@hooks/useMe';
import { useMemberTaskNudge } from '@hooks/useMemberTaskNudge';
import { useOnboardingActive } from '@hooks/useOnboardingActive';
import { usePreviousValue } from '@hooks/usePreviousValue';
import { useProjectTutorial } from '@hooks/useProjectTutorial';
import useSearch from '@hooks/useSearch';
import useTagInput from '@hooks/useTagInput';
import { ChatsStackParamList } from '@navigation/chats/chats-stack';
import theme from '@themes/theme';
import { replyJumpSearchTerm } from '@utils/chat/replyJumpSearchTerm';
import { pixelSizeHorizontal } from '@utils/normalise';
import { consumePendingOpenAttachments } from '@utils/notificationOpenAttachments';
import {
  firstNameOf,
  isSupportUser,
  parseStartConversationPending,
  resolveStartConversationFlow,
  START_CONVERSATION_PENDING_KEY,
  START_CONVERSATION_SEEN_KEY,
} from '@utils/startConversationTutorial';
import { AsyncStorage } from '@utils/storage';
import { firstTagProjectNameOf } from '@utils/uploadTagOnboarding';

// Type-only: ChatMessageData imports this file, so no runtime import back.
import type { OlderPageStatus } from './ChatMessageData';

type MyChatDetails = {
  messages: Message[];
  fetchAfterCursor: (pageSize?: number) => Promise<OlderPageStatus>;
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  /**
   * TA-10885: the messages query failed. Forwarded to ChatMessageList, which
   * shows an explicit offline/error state when it has no cached messages to
   * render instead — previously that combination drew a blank thread.
   */
  loadError?: boolean;
  chatId: Chat['id'];
  messageIdNeedScrollTo?: string;
  messageId?: string;
  showKeyboardFocus?: boolean;
  hasNextPage?: boolean;
};

const callback = () => {};
// Composer top border (1) + tag box marginTop `xs` + border (1) + paddingTop `xs`.
const TAG_CHIP_TOP_INSET = 1 + theme.spacing.xs + 1 + theme.spacing.xs;

// TA-11029: a quoted-reply jump pages back through the thread in pages this
// big (the backend's per-page max) rather than the 20 a normal scroll loads,
// so a far-back original is reached in a handful of requests.
export const REPLY_JUMP_PAGE_SIZE = 100;
// No reply jump — paging back and then scrolling to the original — spins
// longer than this. Past it the user is told and pointed at chat search
// instead (PM decision, TA-11029).
export const REPLY_JUMP_TIMEOUT_MS = 30_000;

/**
 * Pure factory for the beforeRemove guard — exported for unit tests only.
 * The component uses an inline handler that also dispatches StackActions.pop(1)
 * to explicitly dismiss my-tag-modal when it is present in the stack (TA-10398).
 */
export const createTagModalBackHandler =
  (isOpen: boolean, setOpen: (v: boolean) => void) =>
  (e: { preventDefault(): void }) => {
    if (!isOpen) return;
    // my-tag-modal (animationEnabled:false) has no interactive pop, so iOS
    // edge swipes fall through to Chat Detail. Prevent the swipe from popping
    // Chat Detail and dismiss the modal instead so the user stays in-context.
    e.preventDefault();
    setOpen(false);
  };

const MyChatDetails: React.FC<MyChatDetails> = ({
  messages,
  fetchAfterCursor,
  loading,
  refreshing,
  onRefresh,
  loadError = false,
  chatId,
  messageIdNeedScrollTo,
  messageId = '',
  showKeyboardFocus,
  hasNextPage = true,
}) => {
  const { client } = useApolloClient();
  const { t } = useTranslation();
  const { getChatAvatarLabel } = useChatAvatar();
  const navigation =
    useNavigation<StackNavigationProp<ChatsStackParamList, 'chat-detail'>>();
  const insets = useSafeAreaInsets();
  const { me } = useMe();
  // TA-10039: gate every onboarding coachmark/nudge/tooltip in this screen
  // behind the server-computed onboardingComplete flag.
  const onboardingActive = useOnboardingActive();
  // TA-10920: an account that joined through an invitation follows the member
  // (Carlos) flow only, even in a DM it created itself.
  const isInvitedAccount = useIsInvitedNewUser();
  // here we need to query the data to get owner info
  // TA-10885: offline the full GetChat selection set may never have been
  // cached, and the network request cannot fill the gap. Partial cached data
  // (the list-row fields) still renders a real header instead of an empty one,
  // and errorPolicy 'all' keeps it rather than discarding it with the network
  // error. Every read below is already optional-chained.
  const { data: chatData } = useGetChatQuery({
    fetchPolicy: 'cache-first',
    returnPartialData: true,
    errorPolicy: 'all',
    variables: { id: chatId },
    skip: !chatId,
  });
  const { getChat: chat } = chatData || {};

  // Owner (task assigner) vs invitee. The numbered "Step n/3" counters in the
  // tag-onboarding tooltips are shown to the invitee only; the owner sees the
  // same title/description without the step indicator (per design).
  const isChatOwner = !!me?.id && me.id === chat?.owner?.id;

  const requestAttributes = {
    requestType: UserRequestCategory.MemberAddition,
    userRequestableId: chat?.id ?? '',
    userRequestableType: UserRequestable.Chat,
  };
  const { data: pendingRequestData } = useListPendingUserRequestsQuery({
    variables: {
      attributes: requestAttributes,
    },
    skip: !chat?.id,
    fetchPolicy: 'cache-and-network',
  });

  const isCurrentUserRemovedFromGroup =
    !!chat?.isGroupChat &&
    !!me?.id &&
    Array.isArray(chat?.users) &&
    !chat.users.some((user) => user?.id === me.id);
  const isGroupChatAccessRevoked =
    !!chat?.isGroupChat && chat?.friendRequestAccepted === false;

  const hasCurrentUserSentMessage = useMemo(
    () => messages.some((m) => !!m.authorId && m.authorId === me?.id),
    [messages, me?.id]
  );
  const hasOtherMemberSentMessage = useMemo(
    () => messages.some((m) => !!m.authorId && m.authorId !== me?.id),
    [messages, me?.id]
  );
  const hasCurrentUserSharedFile = useMemo(
    () =>
      messages.some(
        (m) =>
          !!m.authorId &&
          m.authorId === me?.id &&
          m.attachments?.some((a) => a.__typename === 'Document')
      ),
    [messages, me?.id]
  );

  const { chatId: nudgeChatId, markOtherMemberSentInNudgeChat } =
    useMemberTaskNudge();

  // Persist the "other member has sent" flag so it survives pagination resets
  useEffect(() => {
    if (hasOtherMemberSentMessage && chatId === nudgeChatId) {
      markOtherMemberSentInNudgeChat();
    }
  }, [
    hasOtherMemberSentMessage,
    chatId,
    nudgeChatId,
    markOtherMemberSentInNudgeChat,
  ]);

  const currentUserPendingInvite =
    pendingRequestData?.listPendingUserRequests.filter(
      (t) => t.user?.id === me?.id && t.canCurrentUserDecide === true
    ) ?? [];
  const shouldShowPendingInviteCard =
    chat?.isGroupChat &&
    chat?.owner?.id !== me?.id &&
    !chat?.users?.some((u) => u.id === me?.id) &&
    chat?.isPendingRequest === true &&
    currentUserPendingInvite.length > 0;

  const shouldShowNoLongerMessage =
    !!chat &&
    !shouldShowPendingInviteCard &&
    (Boolean(chat?.leaveOn) ||
      isCurrentUserRemovedFromGroup ||
      isGroupChatAccessRevoked);

  const shouldRenderChatForm =
    !!chat &&
    !shouldShowPendingInviteCard &&
    (Boolean(chat?.isGroupChat) ||
      Boolean(chat?.friendRequestAccepted) ||
      shouldShowNoLongerMessage);
  // Mirrors the disableInput prop passed to MyChatForm. When true, the chat
  // input bar is replaced by a status message and the tutorial tooltips that
  // anchor to the input are not accessible.
  const isChatInputDisabled =
    !!chat?.settings?.archivedAt || shouldShowNoLongerMessage;
  const [showTagModalSheet, setShowTagModalSheet] = useState(false);
  const tagModalPushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const bottomSheetRef = useRef<BottomSheet>(null);
  const [inputHeight, setInputHeight] = useState<number>(0);
  const [nudgeFocusTrigger, setNudgeFocusTrigger] = useState(0);
  const openChatAttachmentsRef = useRef<(() => void) | null>(null);
  const handleRegisterOpenAttachments = useCallback((fn: () => void) => {
    openChatAttachmentsRef.current = fn;
  }, []);
  const prefillChatInputRef = useRef<((text: string) => void) | null>(null);
  const handleRegisterPrefill = useCallback((fn: (text: string) => void) => {
    prefillChatInputRef.current = fn;
  }, []);

  // "Say hi to your team" → Start Chat handoff (TaskDetail): auto-focus the
  // input, show the one-time "Start a Conversation" tooltip, and surface a
  // success label after the first message is sent.
  const [startConvTooltipMounted, setStartConvTooltipMounted] = useState(false);
  const [startConvTooltipVisible, setStartConvTooltipVisible] = useState(false);
  const startConvFlowActiveRef = useRef(false);
  // Task-assigned handoffs delay the tooltip (TASK ASSIGNED card lands first).
  const startConvTooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const clearStartConvTooltipTimer = useCallback(() => {
    if (startConvTooltipTimerRef.current) {
      clearTimeout(startConvTooltipTimerRef.current);
      startConvTooltipTimerRef.current = null;
    }
  }, []);
  // Transient success label shown above the composer for 3s after an
  // onboarding-flow message is sent (first message / first tag).
  const [composerSuccessMsg, setComposerSuccessMsg] = useState('');
  const composerSuccessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  // "Get a site photo" nudge — appears once the first-message success label
  // fades, prompting the owner to request a photo from the crew.
  const [showPhotoNudge, setShowPhotoNudge] = useState(false);
  // "Tag this message" nudge — appears after the photo-request message is
  // prefilled, highlighting the # button so the user tags before sending.
  const [showTagNudge, setShowTagNudge] = useState(false);

  // First-attachment tag onboarding — Step 3/3 "That's a tag" over the
  // composer once a tag was picked in my-tag-modal.
  const { uploadTagOnboardingStep, setUploadTagOnboardingStep } =
    useChatInput();
  const { tags } = useTagInput();
  const [tagPlacedTooltipVisible, setTagPlacedTooltipVisible] = useState(false);

  useEffect(() => {
    if (uploadTagOnboardingStep === 'tagPlaced') {
      setTagPlacedTooltipVisible(true);
    }
  }, [uploadTagOnboardingStep]);

  // Tag picked in my-tag-modal: when this screen regains focus (the modal
  // closes), re-focus the input so the keyboard is up next to the placed tag.
  // useFocusEffect (not useEffect): the step changes while the modal still
  // covers this screen — focusing then would race the modal dismissal.
  useFocusEffect(
    useCallback(() => {
      if (uploadTagOnboardingStep === 'tagPlaced') {
        setNudgeFocusTrigger((n) => n + 1);
      }
    }, [uploadTagOnboardingStep])
  );

  // my-tag-modal dismissed — sync local state by watching the navigation route
  // list (TA-10398). The 'focus' event does not fire when a transparentModal
  // above this screen is dismissed, so 'state' is used instead: it fires on
  // every stack change and lets us check whether my-tag-modal is still present.
  // my-tag-modal is a sibling route of the parent ChatsStack, not of this
  // nested chat-detail-z navigator, so both the listener and the route check
  // must go through getParent() — checking this navigator's own state never
  // sees the route at all.
  useEffect(() => {
    const parent = navigation.getParent();
    if (!parent) return;
    return parent.addListener('state', () => {
      const routes = parent.getState()?.routes ?? [];
      if (!routes.some((r) => r.name === 'my-tag-modal')) {
        setShowTagModalSheet(false);
      }
    });
  }, [navigation]);

  // Abandoning the chat mid-flow abandons the (one-time) onboarding — don't
  // leak a stale step into the next chat screen.
  useEffect(() => {
    return () => {
      setUploadTagOnboardingStep('idle');
    };
  }, []);

  const invitedMember = chat?.isGroupChat
    ? undefined
    : chat?.users?.find((u) => u.id !== me?.id);
  const isHelpdeskChat = isSupportUser(invitedMember);

  // Tag-pill coachmark ("All your work is saved here") — one-time, shown by
  // TagsCollection on the first chip tap; tap is marked seen server-side.
  // Suppressed in the TaskTag Support chat like every tutorialization flow.
  const [updateOnboardingState] = useUpdateOnboardingStateMutation();
  // Truthy-negation (not `=== false`) to match web: a null/undefined
  // tagPillCoachmarkShown (e.g. the owner's onboardingState) must still enable
  // the coachmark, otherwise it only ever shows for accounts with an explicit
  // false. Only a true value (already seen) suppresses it.
  const tagPillCoachmarkEnabled =
    onboardingActive &&
    !!me &&
    !me?.onboardingState?.tagPillCoachmarkShown &&
    !isHelpdeskChat;
  const handleTagPillCoachmarkSeen = useCallback(() => {
    updateOnboardingState({
      variables: { attributes: { tagPillCoachmarkShown: true } },
      refetchQueries: [{ query: GetMeDocument }],
    });
  }, [updateOnboardingState]);

  // Re-read the handoff on every screen focus, not just mount: when Start
  // Chat resolves to an already-mounted chat (the user came from this very
  // chat via View Task), the screen is focused again without remounting.
  const [startConvPending, setStartConvPending] =
    useState<ReturnType<typeof parseStartConversationPending>>(null);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;

      (async () => {
        try {
          const pendingRaw = await AsyncStorage.getItem(
            START_CONVERSATION_PENDING_KEY
          );
          if (cancelled) return;
          const pending = parseStartConversationPending(pendingRaw);
          if (pending) setStartConvPending(pending);
        } catch {
          // storage read errors — skip the tutorial
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [])
  );

  // Apply the handoff once the chat data and the input form are ready.
  useEffect(() => {
    if (!startConvPending || !chat || !me) return;
    if (!shouldRenderChatForm || isChatInputDisabled) return;
    if (startConvFlowActiveRef.current) return;

    let cancelled = false;

    (async () => {
      try {
        const seen = await AsyncStorage.getItem(START_CONVERSATION_SEEN_KEY);
        if (cancelled) return;

        const { shouldFocusInput, shouldShowTooltip, shouldClearPending } =
          resolveStartConversationFlow({
            pending: startConvPending,
            alreadySeen: seen === 'true',
            chatId,
            isGroupChat: !!chat.isGroupChat,
            isHelpdeskChat,
            firstChatSent: me?.onboardingState?.firstChatSent === true,
          });
        // Handoff targets another chat — keep it in storage so the right
        // chat screen can consume it. A handoff pinned to this chat that we
        // still skip (group/helpdesk) is dropped instead.
        if (!shouldFocusInput) {
          if (shouldClearPending) {
            setStartConvPending(null);
            AsyncStorage.setItem(START_CONVERSATION_PENDING_KEY, '').catch(
              () => {}
            );
          }
          return;
        }

        // Sanitize: storage is untrusted — coerce to a finite, non-negative
        // delay so a corrupt/string value can't yield NaN or a negative timer.
        const rawDelay = Number(startConvPending.tooltipDelayMs);
        const tooltipDelayMs =
          Number.isFinite(rawDelay) && rawDelay > 0 ? rawDelay : 0;
        setStartConvPending(null);
        AsyncStorage.setItem(START_CONVERSATION_PENDING_KEY, '').catch(
          () => {}
        );
        // Focus the composer alongside the tooltip so the keyboard is up when
        // the "Start a Conversation" hint appears. Idempotent (counter bump).
        const focusInput = () => setNudgeFocusTrigger((n) => n + 1);
        if (shouldShowTooltip) {
          startConvFlowActiveRef.current = true;
          AsyncStorage.setItem(START_CONVERSATION_SEEN_KEY, 'true').catch(
            () => {}
          );
          const showTooltip = () => {
            // Mount immediately so the component is ready; delay visibility
            // by 2s so the "Put someone on it" JoinRequest tooltip (if present)
            // can appear first — if it is active when the timer fires, skip
            // showing this tooltip to avoid two overlapping hints.
            setStartConvTooltipMounted(true);
            clearStartConvTooltipTimer();
            startConvTooltipTimerRef.current = setTimeout(
              () => {
                startConvTooltipTimerRef.current = null;
                if (!startConvFlowActiveRef.current) return;
                // TA-10921 #22: focus once, as the tooltip appears. A focus at
                // mount was lost while the screen settled on iOS, so the
                // keyboard flashed up, closed, then reopened with the tooltip.
                focusInput();
                if (!isAssignTaskTooltipActive()) {
                  setStartConvTooltipVisible(true);
                }
              },
              tooltipDelayMs > 0 ? 0 : 2000
            );
          };
          if (tooltipDelayMs > 0) {
            // Delayed (task-assigned) handoff: focus waits for the TASK
            // ASSIGNED card to land, then pops the keyboard with the tooltip.
            clearStartConvTooltipTimer();
            startConvTooltipTimerRef.current = setTimeout(() => {
              startConvTooltipTimerRef.current = null;
              // Sending the first message before the delay elapses ends the
              // flow — don't pop the tooltip over a started conversation.
              if (startConvFlowActiveRef.current) showTooltip();
            }, tooltipDelayMs);
          } else {
            showTooltip();
          }
        } else {
          // Focus-only handoff (tooltip already seen) — bring keyboard up now.
          focusInput();
        }
      } catch {
        // storage read errors — skip the tutorial
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    startConvPending,
    chat?.id,
    me?.id,
    chatId,
    shouldRenderChatForm,
    isChatInputDisabled,
    isHelpdeskChat,
  ]);

  // Fade the onboarding tooltips out once the user starts typing (3s for
  // "Start a Conversation" per Figma, TA-10921 #23; 2s for "That's a tag").
  // Typing also cancels a not-yet-shown delayed tooltip — don't pop it over
  // a user who is already composing.
  const handleChatFirstActivity = useCallback(() => {
    clearStartConvTooltipTimer();
    setStartConvTooltipVisible((visible) => (visible ? false : visible));
    setTagPlacedTooltipVisible((visible) => (visible ? false : visible));
  }, [clearStartConvTooltipTimer]);

  const showComposerSuccessMsg = (msg: string, onDone?: () => void) => {
    setComposerSuccessMsg(msg);
    if (composerSuccessTimerRef.current) {
      clearTimeout(composerSuccessTimerRef.current);
    }
    composerSuccessTimerRef.current = setTimeout(() => {
      setComposerSuccessMsg('');
      onDone?.();
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (composerSuccessTimerRef.current) {
        clearTimeout(composerSuccessTimerRef.current);
      }
      if (startConvTooltipTimerRef.current) {
        clearTimeout(startConvTooltipTimerRef.current);
      }
      if (tagModalPushTimerRef.current) {
        clearTimeout(tagModalPushTimerRef.current);
      }
    };
  }, []);

  useFocusEffect(
    useCallback(() => {
      let isCancelled = false;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let attemptsRemaining = 10;

      const tryOpenPendingAttachments = () => {
        if (isCancelled) {
          return;
        }

        const openAttachments = openChatAttachmentsRef.current;

        if (!openAttachments) {
          if (attemptsRemaining <= 0) {
            return;
          }

          attemptsRemaining -= 1;
          timeoutId = setTimeout(tryOpenPendingAttachments, 100);
          return;
        }

        if (consumePendingOpenAttachments(chatId)) {
          openAttachments();
        }
      };

      tryOpenPendingAttachments();

      return () => {
        isCancelled = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }, [chatId])
  );

  const listRef = useRef<ChatMessageListHandle>(null);
  const { setSelectedMessage } = useChatInput();
  const [filteredMessages, setFilteredMessages] = useState<Message[]>([
    ...messages,
  ]);

  const [, setFilterVal] = useState('');
  const {
    search,
    filter,
    setSearch,
    setIsSearching,
    isSearching,
    isSearchPreview,
    setIsSearchPreview,
    unavailableMessageIds,
  } = useSearch();
  const [isTagSelected, setTagSelected] = useState(false);
  const [isFilesSelected, setFilesSelected] = useState(false);

  const [repliedFromMessageId, setRepliedFromMessageId] = useState('');
  const [repliedClicked, setRepliedClicked] = useState<Message | null | string>(
    null
  );
  // Bumped when a jump's page request settles, so the walk below re-checks
  // even if that page changed nothing it depends on (TA-11029).
  const [replyJumpPageCount, setReplyJumpPageCount] = useState(0);
  const [showNewMessageIndicator, setShowNewMessageIndicator] = useState(false);
  const [firstNewMsgId, setFirstNewMsgId] = useState('');
  // The original a quoted-card tap is jumping to, from the tap until the list
  // reports the scroll landed (or gave up). The walk finding the original is
  // not the end of the jump: the scroll to it can still fail. The ref is read
  // by the list's settle callback, which can fire inside the same handler
  // that set it (TA-11029).
  const [replyJumpTargetId, setReplyJumpTargetIdState] = useState<string>();
  const replyJumpTargetIdRef = useRef<string | undefined>(undefined);
  // The original's text, to prefill chat search if the jump can't reach it.
  // Captured at the tap: by then the walk may have cleared repliedClicked.
  const replyJumpSearchTermRef = useRef('');
  const setReplyJumpTargetId = (id: string | undefined) => {
    replyJumpTargetIdRef.current = id;
    setReplyJumpTargetIdState(id);
  };

  const { body, tagsCollection } = { body: '', tagsCollection: [] };
  const [globalSearch, { data, loading: loadingSearch }] = useSearchLazyQuery();
  const [canLoadMore, setCanLoadMore] = useState(false);
  const [searchFrom, setSearchFrom] = useState(0);

  const {
    isVisibleForChat,
    chatTutorialState,
    setChatTutorialStateAndInStorage,
    setIsVisibleForChat,
  } = useChatTutorial();

  const { projectTutorialState, isVisible, setIsVisible } =
    useProjectTutorial();

  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const keyboardVisibleRef = useRef(false);
  const { keyboardHeight, keyboardVisible } = useKeyboardHeight();
  const [completeWebTutorialFlows] = useCompleteWebTutorialFlowsMutation({
    refetchQueries: [{ query: GetMeDocument }, 'getMe'],
  });

  useEffect(() => {
    if (Platform.OS !== 'android') return;
    const showSub = Keyboard.addListener('keyboardDidShow', () => {
      keyboardVisibleRef.current = true;
    });
    const hideSub = Keyboard.addListener('keyboardDidHide', () => {
      keyboardVisibleRef.current = false;
    });
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  useEffect(() => {
    // Only show the tutorial overlay when the chat form is rendered and its
    // input is active. If the form is not rendered (e.g. pending 1:1 request
    // where friendRequestAccepted=false) or the input is disabled (archived /
    // left chat), the tutorial tooltip UI inside MyChatForm is inaccessible,
    // and showing the fullscreen ProjectOverlay would produce a black screen
    // with no way to continue.
    if (
      isVisibleForChat &&
      shouldRenderChatForm &&
      !isChatInputDisabled &&
      (chatTutorialState === '2' ||
        chatTutorialState === '4' ||
        chatTutorialState === '7')
    ) {
      setShowOverlay(true);
      return;
    }
    setShowOverlay(false);
  }, [
    isVisibleForChat,
    chatTutorialState,
    shouldRenderChatForm,
    isChatInputDisabled,
  ]);

  useFocusEffect(
    React.useCallback(() => {
      if (Platform.OS === 'android') {
        const onBackPress = () => {
          if (keyboardVisibleRef.current) {
            Keyboard.dismiss();
            return true;
          }
          navigation.navigate('chats-home');
          return true;
        };

        const backHandler = BackHandler.addEventListener(
          'hardwareBackPress',
          onBackPress
        );

        return () => backHandler.remove();
      }
    }, [navigation])
  );

  // `from` is an explicit argument rather than read from the `searchFrom`
  // state closure: `setSearchFrom` is not synchronous, so a caller that just
  // reset the offset (setSearchFrom(0)) and immediately invokes this function
  // in the same tick would otherwise still read the PRE-reset value here,
  // sending the reset-to-0 fetch with the stale offset (TA-10169, same bug
  // class fixed in ChatListDrawer.web.tsx PR #5529).
  const globalSearchCall = async (term: string, from: number) => {
    try {
      const queryResult = await globalSearch({
        variables: {
          term,
          size: 20,
          from,
          chatId: chatId,
          includeMessages: true,
          includeDocuments: false,
          includeProjects: false,
          includeTasks: false,
          tags: isTagSelected,
          files: isFilesSelected,
          media: false,
        },
      });
      const searchData = queryResult.data?.search;
      if (searchData) {
        if (
          searchData.length &&
          searchData[0].total > from + searchData.length
        ) {
          setSearchFrom(from + searchData.length);
          setCanLoadMore(true);
        } else {
          setCanLoadMore(false);
        }
      }
    } catch {
      // ignore search errors
    }
  };

  useEffect(() => {
    if (me?.id === '114' || me?.id === '281' || me?.id === '196') {
      Sentry.captureMessage(
        'For Debugging TA-4386 Issue : MyChatDetail ::: chatId ' +
          JSON.stringify(chatId) +
          ' message length ::' +
          JSON.stringify(messages.length)
      );
    }
  }, []);

  // onSearch is read from a ref inside useDebouncedSearch (see hook source),
  // so this closure doesn't need to be memoized/ref-wrapped to stay fresh —
  // the hand-rolled debounce this replaces had to be recreated on every
  // [search, isTagSelected, isFilesSelected] change to avoid a stale
  // closure; that workaround is no longer necessary (TA-10169).
  const handleGlobalSearch = (term: string) => {
    setSearchFrom(0);
    setCanLoadMore(false);
    // Pass 0 explicitly — do not rely on the `searchFrom` state just reset
    // above, which is not yet updated in this closure (TA-10169).
    globalSearchCall(term, 0);
  };

  const { isPending, isBelowThreshold, hasSearched, gatedTerm } =
    useDebouncedSearch(search, handleGlobalSearch);
  // The filter-toggle effect below reads gatedTerm/globalSearchCall but is
  // deliberately keyed only on [isFilesSelected, isTagSelected] (not
  // gatedTerm itself — see that effect's own comment). Unlike ChatDetail.tsx,
  // globalSearchCall here is a plain function (not useCallback-memoized), so
  // it can't be added to the deps array without a NEW reference every render
  // making the effect refire on every render. Refs updated every render let
  // the effect read the CURRENT term/callback without being keyed on either
  // (TA-10169).
  const latestGatedTermRef = useRef(gatedTerm);
  latestGatedTermRef.current = gatedTerm;
  const globalSearchCallRef = useRef(globalSearchCall);
  globalSearchCallRef.current = globalSearchCall;
  // Safe merged-loading form: gating the whole expression on
  // !isBelowThreshold first (rather than `loadingSearch || isPending || ...`)
  // stops a request still resolving right as the term drops below threshold
  // from leaking a stale spinner through in browse mode (TA-10169).
  const isSearchResultLoading =
    !isBelowThreshold && (loadingSearch || isPending || !hasSearched);

  useListRecentProjectsTasksFromQuery({
    chatId: chatId,
    excludeCompleted: true,
    collaboratorsOnly: true,
  });

  // Reset the load-more offset whenever the term drops back below
  // threshold — useDebouncedSearch itself only tracks the debounce window,
  // not this component's own pagination state (TA-10169).
  useEffect(() => {
    if (isBelowThreshold) {
      setSearchFrom(0);
      setCanLoadMore(false);
    }
  }, [isBelowThreshold]);

  // Closes search preview mode on every keystroke (not just threshold
  // crossings) and on unmount — unrelated to the debounce gating above, so
  // kept keyed on the raw live `search` term exactly as before (TA-10169).
  useEffect(() => {
    return () => {
      setIsSearchPreview(false);
    };
  }, [search, setIsSearchPreview]);

  const openEditMessageModal = (message: Message) => {
    setSelectedMessage(message);
    // TA-10443: `Message`'s cache key is `clientId` (see initializeCache.ts),
    // not `id` — `identify(message)` fails whenever the message object
    // handed to this callback is missing `__typename`/`clientId`, even
    // though the rest of the object looks complete. Fall back to building
    // the identifier from `clientId` directly so a partially-shaped message
    // object doesn't silently block the modal.
    const cacheIdentifier =
      client?.cache.identify(message) ??
      (message?.clientId
        ? client?.cache.identify({
            __typename: 'Message',
            clientId: message.clientId,
          })
        : undefined);

    // TA-10443: message long-press intermittently no-ops with no error on
    // native. If `cache.identify` can't resolve an identifier (e.g. a
    // partially-normalized cache entry), this silently skips navigation —
    // breadcrumb lets a future Sentry event show whether this branch fired.
    Sentry.addBreadcrumb({
      category: 'chat.longPress',
      message: cacheIdentifier
        ? 'Long-press attempting to open edit-message modal'
        : 'Long-press no-op: cache.identify returned no identifier',
      data: {
        messageId: message?.id,
        cacheIdentifier: cacheIdentifier ?? null,
        hasClient: !!client,
      },
      level: cacheIdentifier ? 'info' : 'warning',
    });

    if (cacheIdentifier) {
      // TA-10443: `navigate` no-ops (no transition, no remount) when the
      // target route is already on top of the stack — a known React
      // Navigation gotcha. If a prior dismissal of this modal didn't fully
      // pop it (interrupted transition, backgrounded app mid-close), the
      // next long-press would silently do nothing until app restart resets
      // the navigator. `push` always mounts a fresh instance regardless of
      // current stack state.
      navigation.push('edit-message-highlight-modal', {
        cacheIdentifier,
      });
    }
  };

  const openTagModalSheet = () => {
    setShowTagModalSheet(true);
    if (tagModalPushTimerRef.current) {
      clearTimeout(tagModalPushTimerRef.current);
    }
    tagModalPushTimerRef.current = setTimeout(() => {
      tagModalPushTimerRef.current = null;
      // TA-10443: see openEditMessageModal above — `push` guarantees a fresh
      // modal mount even if a previous instance didn't fully pop off the
      // stack, which `navigate` would otherwise silently no-op on.
      navigation.push('my-tag-modal', { chatId, isFrom: 'Chat' });
    }, 50);
  };

  const closeTagModalSheet = () => {
    if (tagModalPushTimerRef.current) {
      clearTimeout(tagModalPushTimerRef.current);
      tagModalPushTimerRef.current = null;
    }
    setShowTagModalSheet(false);
  };

  const toggleTagModalSheet = useCallback(
    (value: boolean | undefined = undefined) => {
      if (value == undefined) {
        showTagModalSheet ? closeTagModalSheet() : openTagModalSheet();
        return;
      }
      value ? openTagModalSheet() : closeTagModalSheet();
    },
    [showTagModalSheet]
  );

  useEffect(() => {
    if (messageId) {
      setRepliedClicked('true');
      setRepliedFromMessageId(messageId);
    }
  }, [messageId]);

  // TA-11078: the target turned out to be deleted (ChatMessageData marked it
  // unavailable and left preview). Drop the jump the effect above started —
  // it only ends on a scroll or backToChat, and without that its spinner
  // would sit over the normal thread for good.
  useEffect(() => {
    if (messageId && unavailableMessageIds.includes(messageId)) {
      setRepliedClicked('');
      setRepliedFromMessageId('');
    }
  }, [messageId, unavailableMessageIds]);

  const scrollToItem = (item: Message | null | undefined) => {
    if (!item?.id) return;

    // TA-10634: the raw FlatList.scrollToItem requires the passed item to
    // be === (by reference) an entry in the list's own `data` array.
    // ChatMessageList's propagateReadReceipts() clones sender messages that
    // need a backfilled read receipt, so the object filteredMessages holds
    // here can be a different reference than what's actually rendered —
    // scrollToItem then silently does nothing. scrollToMessageId resolves
    // the target by id against ChatMessageList's own rendered list instead.
    // Optional for the same stale-bundle reason as cancelScrollToMessage
    // (see endReplyJump).
    listRef.current?.scrollToMessageId?.(item.id, true);
  };

  useEffect(() => {
    if (
      filteredMessages.length > 0 &&
      !repliedFromMessageId &&
      repliedClicked
    ) {
      const item = filteredMessages?.find((filterMessage) => {
        if (
          typeof repliedClicked !== 'string' &&
          repliedClicked?.replyMessage?.id
        ) {
          return filterMessage?.id === repliedClicked?.replyMessage?.id;
        } else if (messageId) {
          return filterMessage?.id === messageId;
        } else {
          return filterMessage?.id === repliedClicked?.id;
        }
      });

      setRepliedFromMessageId(item?.id || '');

      if (!item?.id) {
        if (!isBelowThreshold || filteredMessages !== messages) {
          // TA-10634: filteredMessages syncs from `messages` via its own
          // separate effect below, one render behind — while `messages`
          // and `hasNextPage` update together, synchronously, from the
          // same Apollo data in ChatMessageData. If the final page (the
          // one containing the target) lands in the same commit
          // hasNextPage flips to false, this effect can still be reading
          // a stale filteredMessages here and wrongly give up right as the
          // target was about to arrive. Do nothing this render instead —
          // filteredMessages is a dep below, so once its sync effect
          // catches up (next render, same tick) this effect re-fires with
          // the current list. Gated on isBelowThreshold: with an active
          // search filter, filteredMessages is the filtered subset and by
          // design never becomes === messages, so this wait would never
          // resolve — fall through to giving up immediately there, as
          // before. TA-11029: checked before paging too — a page that has
          // just landed isn't in filteredMessages yet, and paging again on
          // that stale read would fetch a page past the target. And not while
          // a search is active: filteredMessages is then the search results,
          // never the thread. A reply jump leaves search first (see
          // handleScrollToMessage), so this resolves on the next render.
        } else if (loading) {
          // TA-11029: no page can be requested until the initial query
          // settles (fetchAfterCursor resolves at once as a no-op), and
          // re-checking on that would spin. `loading` is a dep; wait for it.
        } else if (hasNextPage) {
          // TA-11029: walking back 20 at a time took tens of seconds on long
          // threads. Re-check once the page settles: if it was a no-op (a
          // page already in flight) or added nothing, no dep changes, and
          // the walk used to stall here with the spinner up for good.
          const page = fetchAfterCursor(REPLY_JUMP_PAGE_SIZE);
          // TA-11029 QA: the walk loaded pages above the viewport while the
          // list sat still, so it looked like nothing was happening. Climb
          // to the oldest loaded message (the end of the inverted list) each
          // time the walk asks for another page. Requested first, so the
          // list's own onEndReached shares this page rather than starting one.
          listRef.current?.scrollToEnd?.({ animated: true });
          page.then((status) => {
            if (status === 'failed') {
              // Re-requesting the same cursor would just fail again, in a
              // tight loop until the deadline, and then blame the distance.
              setRepliedClicked('');
              endReplyJump();
              Alert.alert(t('models:chat.replyJump.loadFailed'));
            } else if (status === 'empty') {
              // A page that brought nothing new can't get any closer.
              setRepliedClicked('');
              endReplyJump();
              showReplyJumpFailed();
            } else {
              setReplyJumpPageCount((count) => count + 1);
            }
          });
        } else {
          // TA-10634: target message isn't in the loaded window and there
          // are no more pages to fetch — stop retrying so the spinner
          // below doesn't stay stuck forever.
          setRepliedClicked('');
          // TA-11029: the whole thread is loaded and the original isn't in
          // it — deleted since this card was cached (a fresh deleted
          // original isn't tappable at all, see ChatMessage). Say so.
          if (typeof repliedClicked !== 'string') {
            setReplyJumpTargetId(undefined);
            Alert.alert(t('models:chat.replyJump.unavailable'));
          }
        }
      } else {
        setRepliedClicked('');
        scrollToItem(item);
      }
    }
    // messageId and fetchAfterCursor are intentionally excluded: this
    // effect re-invokes the CURRENT fetchAfterCursor closure whenever a
    // real dep below changes anyway, and fetchAfterCursor's own identity
    // (a useCallback keyed on pageInfo/edges/loading in ChatMessageData)
    // changes on essentially any chat activity, not just a new page
    // landing — listing it here made this effect refire (and re-call
    // fetchAfterCursor) on every unrelated realtime update in a busy chat,
    // never converging on the target in a paginated jump (TA-10634 regression).
    // loading: the walk waits while the initial query loads and resumes
    // once it settles (TA-11029).
  }, [
    repliedFromMessageId,
    repliedClicked,
    filteredMessages,
    hasNextPage,
    loading,
    replyJumpPageCount,
  ]);

  const showReplyJumpFailed = () => {
    Alert.alert(
      t('models:chat.replyJump.timeoutTitle'),
      t('models:chat.replyJump.timeoutMessage'),
      [
        { text: t('shared:cancel'), style: 'cancel' },
        {
          text: t('shared:search'),
          // TA-11029: prefill search with the original's text. setSearch
          // also opens search; an original with no text just opens it.
          onPress: () => {
            const term = replyJumpSearchTermRef.current;
            if (term) setSearch(term);
            else setIsSearching(true);
          },
        },
      ]
    );
  };

  // Stops the reply jump in progress, wherever it is: the walk (repliedClicked)
  // and the list's scroll retries. Silent; callers say why if they need to.
  const endReplyJump = () => {
    if (!replyJumpTargetIdRef.current) return;
    setReplyJumpTargetId(undefined);
    setRepliedClicked('');
    // Optional call. The device report "cancelScrollToMessage is not a
    // function" came from a stale bundle (a new MyChatDetail running against
    // an old ChatMessageList after a pull while Metro was running), not from
    // the handle: both methods are attached together in its
    // useImperativeHandle. A mismatched bundle must not crash the timeout
    // before the user is told.
    listRef.current?.cancelScrollToMessage?.();
  };

  // A jump left running would alert over whatever screen is on top 30s later,
  // and its Search would change the hidden chat (TA-11029 review). Leaving the
  // chat, opening search or opening a search preview ends it.
  useFocusEffect(useCallback(() => () => endReplyJump(), []));
  useEffect(() => {
    if (isSearching || isSearchPreview) endReplyJump();
  }, [isSearching, isSearchPreview]);

  // TA-11029: a reply jump that hasn't landed within REPLY_JUMP_TIMEOUT_MS of
  // the tap stops — the walk if it's still paging, the list's scroll retries
  // if it's still scrolling — and the user is pointed at search. Only taps on
  // a quoted card; deep links are out of this ticket's scope.
  useEffect(() => {
    if (!replyJumpTargetId) return;
    const timer = setTimeout(() => {
      endReplyJump();
      showReplyJumpFailed();
    }, REPLY_JUMP_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [replyJumpTargetId]);

  // TA-11029 QA: the walk found a far-back original, the list stepped toward
  // it until its retries ran out, and stopped short with no word to the user.
  const onScrollToMessageSettled = (id: string, landed: boolean) => {
    if (id !== replyJumpTargetIdRef.current) return;
    setReplyJumpTargetId(undefined);
    if (!landed) showReplyJumpFailed();
  };

  const handleScrollToMessage = (message: Message, reply = false) => {
    setRepliedClicked(message);
    setReplyJumpTargetId(reply ? message?.replyMessage?.id : undefined);
    replyJumpSearchTermRef.current = reply
      ? replyJumpSearchTerm(message?.replyMessage?.body)
      : '';

    if (reply && (!isBelowThreshold || firstNewMsgId !== '')) {
      // TA-11029 review: the list isn't showing the live thread. During a
      // search it shows the results (the original may not match), and while
      // the new-message indicator is up it renders a frozen snapshot that
      // pages loaded by the walk never get into, so the scroll silently did
      // nothing. Show the thread, then let the walk effect find and scroll
      // to the original once the list has re-rendered with it.
      if (!isBelowThreshold) setIsSearching(false);
      setFirstNewMsgId('');
      setShowNewMessageIndicator(false);
      setRepliedFromMessageId('');
      return;
    }

    const item = filteredMessages?.find((filterMessage) =>
      reply
        ? filterMessage?.id === message?.replyMessage?.id
        : filterMessage?.id === message.id
    );

    setRepliedFromMessageId(item?.id || '');
    scrollToItem(item);

    if (item?.id) {
      // TA-10634: when the target message is already loaded, the retry
      // effect above never runs (its guard requires !repliedFromMessageId,
      // which is already set here) — clear the spinner flag directly.
      setRepliedClicked('');
    }
  };

  const scrollToReplyMessage = (message: Message) =>
    handleScrollToMessage(message, true);

  useEffect(() => {
    showTagModalSheet
      ? bottomSheetRef.current?.expand()
      : bottomSheetRef.current?.close();
  }, [showTagModalSheet]);

  useEffect(() => {
    if (filter.length > 0) {
      selectedOption(filter.toString());
    } else {
      selectedOption('');
    }
    // isBelowThreshold-gated rather than raw `search` truthiness: Apollo
    // retains the previous response's `data` even once the query stops
    // re-firing below threshold — reading it unguarded would leak a stale
    // search's messages in as if live (TA-10169).
    if (!isBelowThreshold) {
      const filterData = data?.search.map((item) => {
        return item.record;
      }) as Message[] | undefined;
      if (filterData) {
        if (searchFrom > 0) {
          const newList = [
            ...filteredMessages,
            ...filterData.filter(
              (m) => !filteredMessages.some((m1) => m1.id === m.id)
            ),
          ];
          setFilteredMessages(newList);
        } else {
          setFilteredMessages(filterData);
        }
      }
    } else {
      setFilteredMessages(messages);
      if (!wasSearch.current) {
        return;
      } else {
        wasSearch.current = false;
      }
    }
  }, [isBelowThreshold, filter, messages, data]);

  // Toggling the Tags/Files filter doesn't change the search term itself, so
  // useDebouncedSearch's own fire-effect (keyed on the term) won't refire —
  // re-run the already-settled gatedTerm directly against the new filter.
  useEffect(() => {
    if (latestGatedTermRef.current) {
      setSearchFrom(0);
      setCanLoadMore(false);
      // A filter change restarts pagination from scratch, same as a brand
      // new search term — pass 0 explicitly rather than the (not yet
      // updated) `searchFrom` state (TA-10169).
      globalSearchCallRef.current(latestGatedTermRef.current, 0);
    }
  }, [isFilesSelected, isTagSelected]);

  const selectedOption = (type: string) => {
    switch (type) {
      case 'Tags':
        setTagSelected(true);
        setFilesSelected(false);
        break;
      case 'Files':
        setFilesSelected(true);
        setTagSelected(false);
        break;
      default:
        setFilesSelected(false);
        setTagSelected(false);
        return false;
    }
  };

  const lastChar = (string: string) => {
    return string.charAt(string.length - 1);
  };

  const prevMessage = usePreviousValue({ body });

  useEffect(() => {
    if (!body) return;

    const lastCharOfMessage = lastChar(body);

    if (body.includes('#')) {
      setFilterVal(body.slice(body.lastIndexOf('#') + 1));
    }

    // typed a '#', open the modal
    // do not open if '#' present from a draft (prevMessage undefined)
    if (lastCharOfMessage === '#' && prevMessage) {
      // TA-10443: use openTagModalSheet (which uses `push`) so this entry
      // point behaves consistently with the hashtag-button entry point and
      // properly tracks showTagModalSheet for the beforeRemove guard (TA-10398).
      openTagModalSheet();
    } else if (body.trimEnd() !== body) {
      // backspaced into a space, close the modal
      setShowTagModalSheet(false);
    } else if (!body) {
      setShowTagModalSheet(false);
    }
  }, [body]);

  const prevTagsCollection =
    usePreviousValue<TagsCollectionType[]>(tagsCollection);

  useEffect(() => {
    const prevTagsCollectionFlat =
      prevTagsCollection?.flatMap((tagCollection) => {
        return [tagCollection.project, ...tagCollection.tasks];
      }) || [];
    const tagsCollectionFlat =
      tagsCollection?.flatMap((tagCollection) => {
        return [tagCollection.project, ...tagCollection.tasks];
      }) || [];

    if (tagsCollectionFlat.length > prevTagsCollectionFlat.length) {
      setShowTagModalSheet(false);

      setFilterVal('');
    }
  }, [tagsCollection]);

  const onViewableMsgsChanged = (items: ViewToken[]) => {
    if (
      items.some(
        (x) => filteredMessages.length && x.item.id == filteredMessages[0].id
      )
    ) {
      setShowNewMessageIndicator(false);
      setFirstNewMsgId('');
    }
  };
  const wasSearch = useRef(false);

  useEffect(() => {
    if (isSearching) {
      wasSearch.current = true;
    }
  }, [isSearching]);

  const scrollToFirstNewMsg = () => {
    setShowNewMessageIndicator(false);
    let xindex = 0;
    filteredMessages.forEach((v, i) => {
      if (v.id == firstNewMsgId) {
        xindex = i;
        setFirstNewMsgId('');
      }
    });
    filteredMessages.length &&
      listRef.current?.scrollToIndex({
        index: xindex,
        animated: false,
      });
  };

  useEffect(() => {
    const closeTagModalOnBack = (e: { preventDefault(): void }) => {
      if (!showTagModalSheet) return;
      e.preventDefault();
      if (tagModalPushTimerRef.current) {
        clearTimeout(tagModalPushTimerRef.current);
        tagModalPushTimerRef.current = null;
      }
      setShowTagModalSheet(false);
      // my-tag-modal (animationEnabled:false) has no interactive pop, so iOS
      // edge swipes fall through to chat-detail. After preventing that pop,
      // explicitly dismiss my-tag-modal so the user isn't left stuck on the
      // overlay with no gesture path back (TA-10398). my-tag-modal lives in
      // the parent ChatsStack, not this nested navigator, so both the route
      // check and the pop dispatch must target getParent().
      const parent = navigation.getParent();
      const routes = parent?.getState()?.routes ?? [];
      if (routes.some((r) => r.name === 'my-tag-modal')) {
        parent?.dispatch(StackActions.pop(1));
      }
    };
    navigation.addListener('beforeRemove', closeTagModalOnBack);
    return () => navigation.removeListener('beforeRemove', closeTagModalOnBack);
  }, [navigation, showTagModalSheet]);

  const onFormSubmit = () => {
    filteredMessages.length > 0 &&
      listRef.current?.scrollToIndex({
        index: 0,
        animated: false,
      });

    // First onboarding message sent — show the success label for 3s. The
    // copy depends on role in the 1:1: the assigner (chat owner, A) sees
    // "First message sent! 🎉"; the invitee (B) sees the "{name} can see
    // this — you'll hear back soon" reassurance.
    // A reaches this via the Start Chat handoff (startConvFlowActiveRef);
    // B reaches it on their first message in the member-task-nudge chat.
    const isOnboardingFirstMessage =
      startConvFlowActiveRef.current ||
      (!!nudgeChatId && chatId === nudgeChatId && !hasCurrentUserSentMessage);

    // TA-10921 #24/#27: the first message hands off to a banner / success
    // label, not more typing — drop focus so the keyboard doesn't cover it.
    // Not gated on isOnboardingFirstMessage alone: the Member often misses
    // both of its signals — the "Start a Conversation" tooltip is skipped
    // when `assignTooltipSeen` is already set on the device (focus-only
    // handoff), and the nudge chatId is only stored by sendDraftMessage,
    // which runs after this callback. Keyboard.dismiss() also blurs the
    // focused input, so the composer doesn't stay focused.
    const isFirstMessageDuringOnboarding =
      onboardingActive && !hasCurrentUserSentMessage && !isHelpdeskChat;
    if (isOnboardingFirstMessage || isFirstMessageDuringOnboarding) {
      // Deferred: this runs before MyChatForm clears the input. Blurring
      // first lets iOS commit a pending autocorrection via onChangeText
      // after the reset, putting the sent text back in the composer.
      requestAnimationFrame(() => Keyboard.dismiss());
    }

    if (isOnboardingFirstMessage) {
      startConvFlowActiveRef.current = false;
      setStartConvTooltipVisible(false);
      const isAssigner =
        !!me?.id && me.id === chat?.owner?.id && isInvitedAccount === false;
      if (isAssigner) {
        showComposerSuccessMsg(
          t('models:chat.onboarding.firstMessageSent'),
          // Once the success label fades, surface the "Get a site photo" nudge.
          () => setShowPhotoNudge(true)
        );
      } else {
        showComposerSuccessMsg(
          t('models:chat.onboarding.viewTaskMsgSent', {
            name: firstNameOf(invitedMember?.name),
          })
        );
      }
    }

    // First tagged message sent (upload-tag onboarding) — show the
    // "Saved to <project>" label for 3s. Project name is dynamic.
    if (uploadTagOnboardingStep === 'tagPlaced') {
      setUploadTagOnboardingStep('idle');
      setTagPlacedTooltipVisible(false);
      const sentTags = tags(chatId);
      if (sentTags.length > 0) {
        const projectName = firstTagProjectNameOf(sentTags);
        // Owner (assigner) gets the generic completion; the invitee gets the
        // "Saved to <project>" reassurance naming where the tag landed.
        showComposerSuccessMsg(
          isChatOwner || !projectName
            ? t('models:chat.onboarding.firstTagComplete')
            : t('models:chat.onboarding.firstTagSavedTo', { projectName })
        );
      }
    }
  };

  // The list's own infinite scroll (onEndReached).
  const fetchOlderFromList = () => {
    // TA-11029 review: a reply jump drives paging itself (100 at a time) and
    // climbs the list. An onEndReached landing between two of its pages
    // would start a 20-row page that the jump's next request then shares.
    if (replyJumpTargetIdRef.current) return;
    if (isSearching && searchFrom > 0) {
      // Pass the current searchFrom explicitly — this closure is recreated
      // fresh every render, so it always has the up-to-date offset (TA-10169).
      globalSearchCall(gatedTerm, searchFrom);
    } else {
      fetchAfterCursor();
    }
  };

  const showNewMessageIndicatorHandler = (newMsgId: string) => {
    // TA-11029 review: a reply jump climbs away from the bottom. A message
    // arriving mid-jump would freeze the list on a snapshot that the pages
    // still to come never reach, and the scroll to the original would fail.
    if (replyJumpTargetIdRef.current) return;
    if (!isSearching) {
      if (firstNewMsgId === '') {
        setFirstNewMsgId(newMsgId);
      }
      setShowNewMessageIndicator(true);
    } else {
      setShowNewMessageIndicator(false);
    }
  };

  const onPressChatMessage = (message: Message) => {
    scrollToReplyMessage(message);
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
    },
    wrapper: {
      flex: 1,
      paddingBottom:
        Platform.OS === 'android' && keyboardVisible ? 0 : insets.bottom,
    },
    newMessageContainer: {
      width: 150,
      position: 'absolute',
      bottom: inputHeight + 11,
      right: 17,
    },
    newMessageBox: {
      borderRadius: 21,
      height: 35,
      shadowColor: 'black',
      shadowOpacity: 0.12,
      shadowOffset: { width: 2, height: 2 },
      shadowRadius: 12,
    },
    background: {
      opacity: 1,
      position: 'absolute',
      bottom: 0,
      top: 0,
      right: 0,
      left: 0,
    },
    previewBanner: {
      height: 50,
      backgroundColor: theme.colors.grey05,
      justifyContent: 'center',
      alignItems: 'center',
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey02,
    },
  });

  const backToChat = () => {
    setIsSearchPreview(false);
    setSearch('');
    setIsSearching(false);
    setRepliedClicked('');
    setRepliedFromMessageId('');
  };

  if (isSearchPreview) {
    return (
      <>
        {showOverlay && (
          <ProjectOverlay visible={showOverlay} borderRadius='xs' />
        )}
        <Box style={styles.wrapper}>
          <ChatHeader chatId={chatId} />
          <Box flex={1}>
            <ChatMessageList
              chatId={chatId}
              hasNextPage={isSearching ? canLoadMore : hasNextPage}
              firstNewMsgId={firstNewMsgId}
              onViewableMsgItemsChanged={onViewableMsgsChanged}
              list={filteredMessages}
              filterVal={search}
              ref={listRef}
              fetchAfterCursor={fetchOlderFromList}
              loading={loading || isSearchResultLoading}
              refreshing={refreshing}
              loadError={loadError}
              onRetry={onRefresh}
              onLongPress={openEditMessageModal}
              onPress={onPressChatMessage}
              highlightedToGrey01MessageId={repliedFromMessageId}
              messageIdNeedScrollTo={
                isSearchPreview ? messageIdNeedScrollTo : ''
              }
              onEditTagPress={() => {
                callback();
              }}
              onForwardPress={callback}
            />
          </Box>
          <TouchableOpacity
            onPress={backToChat}
            activeOpacity={0.7}
            accessibilityLabel='Exit preview mode'>
            <Box style={styles.previewBanner}>
              <Text
                fontSize={14}
                variant='metadataSecondary'
                fontWeight='500'
                color='grey01'>
                Preview mode – tap here to start chatting
              </Text>
            </Box>
          </TouchableOpacity>
        </Box>
      </>
    );
  }

  const updateChatTutorialState = () => {
    !me?.completedWebTutorialFlows?.includes('chat') &&
      completeWebTutorialFlows({
        variables: {
          flowType: WebTutorialFlow.Chat,
        },
      });
    !me?.completedWebTutorialFlows?.includes('tag_on_chat_panel_open') &&
      completeWebTutorialFlows({
        variables: {
          flowType: WebTutorialFlow.TagOnChatPanelOpen,
        },
      });
    !me?.completedWebTutorialFlows?.includes('tag_on_chat_panel_closed') &&
      completeWebTutorialFlows({
        variables: {
          flowType: WebTutorialFlow.TagOnChatPanelClosed,
        },
      });
  };
  const getPendingInviteCard = () => (
    <Box paddingHorizontal='m' paddingTop='m' paddingBottom='m'>
      <Box alignItems='center' marginBottom='s' flexDirection='row'>
        <Box borderBottomColor='grey02' borderBottomWidth={1} flex={1} />
        <Text variant='metadata' mx='m' color='grey03'>
          {currentUserPendingInvite.length > 0
            ? t('format:timeAgoMessageList', {
                val: currentUserPendingInvite[0].createdAt,
              })
            : ''}
        </Text>
        <Box borderBottomColor='grey02' borderBottomWidth={1} flex={1} />
      </Box>
      <Box flexDirection='row' alignItems='flex-start'>
        <Avatar
          id={chat?.id}
          avatarType='chat'
          size='small'
          disableAvatarPress={true}
          backgroundColor={theme.colors.grey02}
          avatar={chat?.avatar}
          avatarUrl={chat?.avatarUrl}
          isMultiple={true}
          label={chat?.name ?? getChatAvatarLabel(chat)}
        />
        <Box flex={1} marginLeft='s'>
          <Box flexDirection='row'>
            <Text variant='webSmall' color='textPrimary'>
              {chat?.name ?? ''}
            </Text>

            <Text ml='xxs' variant='metadataSecondary' color='grey04'>
              {currentUserPendingInvite.length > 0
                ? t('format:time', {
                    val: currentUserPendingInvite[0].createdAt,
                  })
                : ''}
            </Text>
          </Box>
          <Text marginTop='xs' variant='webBodySecondary' color='textSecondary'>
            {`You’ve been invited by ${
              chat?.owner?.name ?? ''
            } to join this group, this invitation will expire in 7 days.`}
          </Text>
          <Box
            mt='xs'
            flexDirection='row'
            alignItems='center'
            marginHorizontal='xs'
            paddingBottom='s'>
            <AcceptDeclineButtons
              borderRadius='xs'
              acceptIconName='CheckCircle'
              fromChatDetail={true}
              suffix={true}
              requestId={currentUserPendingInvite[0].id}
              toggleAcceptDecline={() => {
                navigation.goBack();
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
  const androidKeyboardPadding =
    Platform.OS === 'android' && keyboardHeight > 0
      ? keyboardHeight + insets.bottom
      : 0;
  // Absolutely positioned overlays ignore the KeyboardAvoidingView's padding,
  // so on iOS they stay where the composer was before the keyboard opened —
  // i.e. hidden behind it. Lift them by the same amount the KAV pads.
  const composerOverlayKeyboardOffset =
    Platform.OS === 'ios'
      ? Math.max(keyboardHeight - insets.bottom, 0)
      : androidKeyboardPadding;

  return (
    <>
      {showOverlay && (
        <ProjectOverlay visible={showOverlay} borderRadius='xs' />
      )}
      <CreatedProjectModal
        visible={chatTutorialState === '8'}
        title='Great Job!'
        description='You’ve laid another foundation. We’ll guide you as you build further.'
        primaryBtnTitle='Go Home'
        secondaryBtnTitle='Learn More Tutorial'
        primaryFn={async () => {
          setChatTutorialStateAndInStorage('9');
          await AsyncStorage.setItem('isChatTutorializationCompleted', 'true');
          updateChatTutorialState();
          if (projectTutorialState === '14' || projectTutorialState === '15') {
            isVisible && setIsVisible(false);
            await AsyncStorage.deleteItem('new_user');
          }
          setTimeout(() => {
            navigation.dispatch(
              CommonActions.reset({
                index: 0,
                routes: [{ name: 'home' }],
              })
            );
          }, 500);
          isVisibleForChat && setIsVisibleForChat(false);
        }}
        secondaryFn={async () => {
          setChatTutorialStateAndInStorage('9');
          await AsyncStorage.setItem('isChatTutorializationCompleted', 'true');
          updateChatTutorialState();
          if (projectTutorialState === '14' || projectTutorialState === '15') {
            isVisible && setIsVisible(false);
            await AsyncStorage.deleteItem('new_user');
          }
          isVisibleForChat && setIsVisibleForChat(false);
          await Linking.openURL(
            `https://youtube.com/shorts/WsDXXA7QY10?si=D1n2fKKpnfeZraZ-`
          );
        }}
      />
      <Box style={styles.wrapper}>
        <ChatHeader
          chatId={chatId}
          friendRequestAccepted={chat?.friendRequestAccepted ?? true}
        />
        {shouldShowPendingInviteCard ? (
          <Box position='absolute' left={0} right={0} bottom={16}>
            {getPendingInviteCard()}
          </Box>
        ) : (
          <KeyboardAvoidingView
            style={{
              flex: 1,
              paddingBottom: androidKeyboardPadding,
            }}
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
            {!loading && repliedClicked && (
              <Box zIndex={99999}>
                <ActivityIndicatorLoading testID='reply-jump-spinner' />
              </Box>
            )}
            <ChatMessageList
              chatId={chatId}
              hasNextPage={isSearching ? canLoadMore : hasNextPage}
              firstNewMsgId={firstNewMsgId}
              showNewMessageIndicator={showNewMessageIndicatorHandler}
              onViewableMsgItemsChanged={onViewableMsgsChanged}
              list={filteredMessages}
              filterVal={search}
              ref={listRef}
              fetchAfterCursor={fetchOlderFromList}
              loading={loading || isSearchResultLoading}
              refreshing={refreshing}
              loadError={loadError}
              onRetry={onRefresh}
              onLongPress={openEditMessageModal}
              onPress={onPressChatMessage}
              highlightedToGrey01MessageId={repliedFromMessageId}
              messageIdNeedScrollTo={
                isSearchPreview ? messageIdNeedScrollTo : ''
              }
              onEditTagPress={() => {
                callback();
              }}
              onForwardPress={callback}
              onScrollToMessageSettled={onScrollToMessageSettled}
              coachmarkEnabled={tagPillCoachmarkEnabled}
              onCoachmarkSeen={handleTagPillCoachmarkSeen}
            />

            {showNewMessageIndicator && (
              <TouchableOpacity
                style={styles.newMessageContainer}
                activeOpacity={0.2}
                onPress={scrollToFirstNewMsg}>
                <Box
                  borderColor='grey02'
                  flexDirection='row'
                  style={styles.newMessageBox}
                  alignItems='center'
                  justifyContent='center'
                  pointerEvents='none'
                  backgroundColor='grey01'>
                  <Text variant='labelSmall' marginLeft='xxs'>
                    New Message
                  </Text>
                  <Icon
                    name='ArrowDown'
                    variant='m'
                    color='textPrimary'
                    marginTop='xxxs'
                  />
                </Box>
              </TouchableOpacity>
            )}
            {chat?.settings?.archivedAt && <Box marginTop='m' />}
            {!chat?.settings?.archivedAt && !isSearching && (
              <>
                <Box flex={1} style={styles.background} pointerEvents='none' />
                {chat?.isGroupChat && (
                  <MemberTaskNudgeBanner
                    currentChatId={chatId}
                    onFocusChatInput={() => setNudgeFocusTrigger((n) => n + 1)}
                    onOpenChatAttachments={() =>
                      openChatAttachmentsRef.current?.()
                    }
                    bottom={inputHeight + theme.spacing.xs}
                    hasSentMessageInChat={hasCurrentUserSentMessage}
                    hasOtherMemberSentMessage={hasOtherMemberSentMessage}
                    hasSharedFileInChat={hasCurrentUserSharedFile}
                  />
                )}
                {startConvTooltipMounted && (
                  <Box
                    position='absolute'
                    bottom={
                      inputHeight +
                      theme.spacing.xs +
                      composerOverlayKeyboardOffset
                    }
                    left={theme.spacing.m}
                    zIndex={1000}
                    pointerEvents='none'>
                    <TooltipPopup
                      isFromChat
                      animate
                      visible={onboardingActive && startConvTooltipVisible}
                      title={t('models:chat.onboarding.assignTooltip.title')}
                      description={t(
                        'models:chat.onboarding.assignTooltip.description'
                      )}
                      pointerPosition='bottom-left'
                      fadeTime={3000}
                    />
                  </Box>
                )}
                {onboardingActive && showPhotoNudge && (
                  <NudgeBanner
                    inline
                    iconName='Camera'
                    title={t('models:chat.onboarding.photoNudge.title')}
                    description={t(
                      'models:chat.onboarding.photoNudge.description'
                    )}
                    ctaLabel={t('models:chat.onboarding.photoNudge.cta')}
                    onCta={() => {
                      setShowPhotoNudge(false);
                      prefillChatInputRef.current?.(
                        t('models:chat.onboarding.photoNudge.message')
                      );
                      // Prompt tagging the prefilled message next (highlights #).
                      setShowTagNudge(true);
                    }}
                  />
                )}
                {onboardingActive && showTagNudge && (
                  // No CTA button — the highlighted # button is the action
                  // (mirrors web ChatOnboardingNudge). Tap # to open the tag list.
                  <Box
                    marginHorizontal='m'
                    marginBottom='xs'
                    padding='s'
                    backgroundColor='black'
                    borderRadius='xs'>
                    <Box
                      flexDirection='row'
                      alignItems='center'
                      marginBottom='xs'
                      style={{ gap: theme.spacing.xs }}>
                      <Box
                        width={32}
                        height={32}
                        borderRadius='m'
                        backgroundColor='transparentGreenDark'
                        alignItems='center'
                        justifyContent='center'>
                        <Icon name='Hash' variant='m' color='greenBrand' />
                      </Box>
                      <Text
                        variant='labelEmphasizedNudgeTitle'
                        style={{ flex: 1 }}>
                        {t('models:chat.onboarding.nudge.title')}
                      </Text>
                    </Box>
                    <Text variant='metadata' color='white'>
                      {t('models:chat.onboarding.nudge.description', {
                        name: invitedMember?.name ?? '',
                      })}
                    </Text>
                  </Box>
                )}
                {onboardingActive &&
                  uploadTagOnboardingStep === 'tagPlaced' && (
                    // Arrow tip 4px above the placed Project tag. The tag
                    // sits TAG_CHIP_TOP_INSET below the composer's top edge
                    // (composer border + tag box margin/border/padding).
                    <Box
                      position='absolute'
                      bottom={
                        inputHeight +
                        composerOverlayKeyboardOffset -
                        TAG_CHIP_TOP_INSET +
                        getTooltipAnchorSpacing()
                      }
                      left={pixelSizeHorizontal(31)}
                      zIndex={1000}
                      pointerEvents='none'>
                      <TooltipPopup
                        isFromChat
                        animate
                        visible={tagPlacedTooltipVisible}
                        title={t('models:chat.onboarding.hashTagStep3.title')}
                        description={t(
                          'models:chat.onboarding.hashTagStep3.description'
                        )}
                        pointerPosition='bottom-left'
                        currentStep={isChatOwner ? undefined : 3}
                        totalSteps={isChatOwner ? undefined : 3}
                        fadeTime={2000}
                      />
                    </Box>
                  )}
                {!!composerSuccessMsg && (
                  // Pulled up 8px to sit closer to the last message, 8px above
                  // the composer. The old -16 top margin overlapped the bubble.
                  <Box
                    marginBottom='xs'
                    alignItems='flex-start'
                    style={{
                      marginTop: -theme.spacing.xs,
                      marginLeft: theme.spacing.xxl + theme.spacing.s,
                    }}>
                    <Text variant='webSmall' color='black'>
                      {composerSuccessMsg}
                    </Text>
                  </Box>
                )}
                <Box
                  onLayout={(e) => {
                    setInputHeight(e.nativeEvent.layout.height);
                  }}>
                  {shouldRenderChatForm && (
                    <MyChatForm
                      isPendingRequest={chat?.isPendingRequest ?? false}
                      showKeyboardFocus={showKeyboardFocus}
                      chatInputFocusTrigger={nudgeFocusTrigger}
                      isGroupChat={chat?.isGroupChat}
                      chatInputHeight={inputHeight}
                      chatId={chatId}
                      chatData={chatData}
                      toggleTagModalSheet={toggleTagModalSheet}
                      onTagChange={setFilterVal}
                      disableInput={isChatInputDisabled}
                      showLeaveMessage={shouldShowNoLongerMessage}
                      onFormSubmit={onFormSubmit}
                      onRegisterOpenAttachments={handleRegisterOpenAttachments}
                      onRegisterPrefill={handleRegisterPrefill}
                      onFirstActivity={handleChatFirstActivity}
                      highlightTagButton={onboardingActive && showTagNudge}
                      onHighlightTagPress={() => setShowTagNudge(false)}
                    />
                  )}
                </Box>
              </>
            )}
          </KeyboardAvoidingView>
        )}
      </Box>
    </>
  );
};

export const MyChatDetail = memo(MyChatDetails);
